// src/pages/ContentDetail/hooks/useContentDetailState.js
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getReaderProfileOnboardingConfig } from '../../../services/configuration-service';
import { 
  publish,
  approve,
  release,
  archive,
  get
} from '../../../services/practice-service';

export const useReadingPracticeDetails = () => {
  const { id, type } = useParams();
  const navigate = useNavigate();
  
  // Check which tab the user is coming from
  const isFromDraftTab = type === 'draft' || false;
  const isFromPublishedTab = type === 'published' || false;
  const isFromApprovedTab = type === 'approved' || false;
  const isFromReleasedTab = type === 'released' || false;
  const isFromArchivedTab = type === 'archived' || false;

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [readingCategories, setReadingCategories] = useState([]);

  const fetchContentDetails = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await get(id);
      setContent(data);
    } catch (err) {
      setError('Failed to fetch content details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch content details on component mount
  useEffect(() => {
    getReaderProfileOnboardingConfig()
      .then(config => {
        if (config && config.readingCategories) {
          setReadingCategories(config.readingCategories);
        }
      })
      .catch(console.error);
    fetchContentDetails();
  }, [id, type, fetchContentDetails]);

  const publishContent = async () => {
    setLoading(true);
    setError(null);

    try {
      await publish(id);
      // Refresh content after publishing
      await fetchContentDetails();
    } catch (err) {
      setError('Failed to publish content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const approveContent = async () => {
    setLoading(true);
    setError(null);

    try {
      await approve(id);
      // Refresh content after approving
      await fetchContentDetails();
    } catch (err) {
      setError('Failed to approve content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const releaseContent = async () => {
    setLoading(true);
    setError(null);

    try {
      await release(id);
      // Refresh content after releasing
      await fetchContentDetails();
    } catch (err) {
      setError('Failed to release content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const archiveContent = async () => {
    setLoading(true);
    setError(null);

    try {
      await archive(id);
      // Refresh content after archiving
      await fetchContentDetails();
    } catch (err) {
      setError('Failed to archive content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle menu
  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // Handle edit
  const handleEdit = () => {
    handleMenuClose();
    navigate(`/reading-practices/${type}/${id}/edit`);
  };

  // Handle delete
  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      const tabIndex = getTabIndex();
      navigate('/', { state: { activeTab: tabIndex } });
    } catch (error) {
      console.error('Delete operation failed:', error);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // Helper function to get tab index based on type
  const getTabIndex = () => {
    switch (type) {
      case 'draft':
        return 0;
      case 'published':
        return 1;
      case 'approved':
        return 2;
      case 'released':
        return 3;
      case 'archived':
        return 4;
      default:
        return 0;
    }
  };

  // Handle print
  const handlePrint = () => {
    handleMenuClose();
    window.print();
  };

  return {
    content,
    loading,
    error,
    tabValue,
    menuAnchor,
    deleteDialogOpen,
    isFromDraftTab,
    isFromPublishedTab,
    isFromApprovedTab,
    isFromReleasedTab,
    isFromArchivedTab,
    setContent,
    setLoading,
    setError,
    setTabValue,
    setMenuAnchor,
    setDeleteDialogOpen,
    handleTabChange,
    handleMenuOpen,
    handleMenuClose,
    handleEdit,
    handlePrint,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    publishContent,
    approveContent,
    releaseContent,
    archiveContent,
    fetchContentDetails,
    getTabIndex,
    readingCategories
  };
};